import { Badge } from '@/components/ui/badge';
import { LocalScenarioProcedure } from '@/lib/scenarioStorage';
import { useBrand } from '@/config/brands';

interface ScenarioReviewPanelProps {
  name: string;
  description: string;
  duration: string;
  severityLevel: number;
  tags: string[];
  procedures: LocalScenarioProcedure[];
  iconColor: string;
}

export function ScenarioReviewPanel({
  name,
  description,
  duration,
  severityLevel,
  tags,
  procedures,
  iconColor,
}: ScenarioReviewPanelProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const totalCost = procedures.reduce((sum, p) => sum + (p.estimatedCost * p.quantity), 0);
  const phases = Array.from(new Set(procedures.map(p => p.phase).filter(Boolean)));

  return (
    <div className={`border rounded-lg p-4 ${isAhp ? 'border-gray-200 bg-gray-50' : 'border-matrix-green/20 bg-black/20'}`}>
      <h3 className="font-mono font-bold text-lg mb-4" style={{ color: iconColor }}>
        {name}
      </h3>
      <p className="text-sm text-muted-foreground mb-4">{description || 'No description'}</p>

      <div className="grid grid-cols-3 gap-4 text-xs mb-4">
        <div>
          <span className="text-muted-foreground">Duration:</span>
          <span className="ml-2 font-mono">{duration || 'Not specified'}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Procedures:</span>
          <span className="ml-2 font-mono">{procedures.length}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Severity:</span>
          <span className="ml-2 font-mono font-bold" style={{
            color: severityLevel <= 3 ? '#4ade80' : severityLevel <= 6 ? '#fbbf24' : '#ef4444'
          }}>
            {severityLevel}/10
            {severityLevel <= 3 && ' \u2705'}
            {severityLevel > 3 && severityLevel <= 6 && ' \u26a0\ufe0f'}
            {severityLevel > 6 && ' \ud83d\udea8'}
          </span>
        </div>
      </div>

      {procedures.length > 0 && (
        <div className="mb-4">
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="text-muted-foreground">Total Estimated Cost:</span>
            <span className={`font-mono font-bold text-lg ${isAhp ? 'text-blue-600' : 'text-matrix-green'}`}>
              ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {phases.length > 1 && (
            <div className="mb-3 space-y-1">
              {phases.map(phase => {
                const phaseCost = procedures
                  .filter(p => p.phase === phase)
                  .reduce((sum, p) => sum + (p.estimatedCost * p.quantity), 0);
                const pct = totalCost > 0 ? (phaseCost / totalCost * 100).toFixed(0) : '0';
                return (
                  <div key={phase} className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>{phase}</span>
                    <span className="font-mono">${phaseCost.toLocaleString(undefined, { maximumFractionDigits: 0 })} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className={`max-h-[200px] overflow-y-auto thin-scrollbar space-y-1.5 mt-3 p-2 rounded border ${
            isAhp ? 'bg-white border-gray-200' : 'bg-black/20 border-matrix-green/10'
          }`}>
            {procedures.map((proc, index) => (
              <div key={index} className={`text-[10px] text-muted-foreground flex items-center justify-between gap-2 p-1.5 rounded ${
                isAhp ? 'hover:bg-blue-50' : 'hover:bg-matrix-green/5'
              }`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Badge variant="outline" className="text-[8px] px-1 py-0">
                    {proc.codeType}
                  </Badge>
                  <span className={`font-mono ${isAhp ? 'text-blue-600' : 'text-matrix-green'}`}>{proc.code}</span>
                  <span className="truncate">{proc.description}</span>
                </div>
                <span className="font-mono text-[9px] whitespace-nowrap">
                  {proc.quantity > 1 && `${proc.quantity}x `}${(proc.estimatedCost * proc.quantity).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Badge key={tag} variant="outline" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
